import React from "react";

import { DateRangePicker, InputWrapper } from "@performyard/py-components";

import EmployeePicker from "../../../../components/EmployeePicker";
import { RadioButtons } from "../../../../components/Inputs/SelectionOptions/RadioButtons";
import FilterPanel from "../../../../components/Panel/FilterPanel";
import Spacer from "../../../../components/Spacer";

import type { WorkspacesFilters } from "../schemas";

import { capitalizeWord } from "../../../../utils/stringUtils";

import { workspacesTableConfig } from "../config";

export const WorkspacesFilterPanel = ({
	isOpen,
	onClose,
	onApplyFilters,
	filters,
	updateForm,
}: {
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: WorkspacesFilters) => void;
	filters: WorkspacesFilters;
	updateForm: (newFilters: Partial<WorkspacesFilters>) => void;
}) => {
	const statusRadioOptions = workspacesTableConfig.statusOptions.map((status) => ({
		value: status,
		label: capitalizeWord(status),
	}));
	const statusRadioValue = statusRadioOptions.find((option) => option.value === filters?.status);

	return (
		<FilterPanel
			isOpen={isOpen}
			onClose={onClose}
			title={"Filters"}
			onSaveFilters={() => {
				onApplyFilters(filters);
				onClose();
			}}
		>
			<EmployeePicker
				label="Created By"
				value={filters?.createdBy ?? ""}
				onChange={(newEmployeeValue) => {
					// Check that newEmployeeValue is SingleEmployeeSchema
					let singleNewEmployeeValue = newEmployeeValue;
					if (Array.isArray(newEmployeeValue)) {
						singleNewEmployeeValue = newEmployeeValue[0];
					}
					if (singleNewEmployeeValue && !("value" in singleNewEmployeeValue)) {
						return;
					}
					updateForm({ createdBy: singleNewEmployeeValue ?? undefined });
				}}
				isClearable
			/>
			<Spacer height={2} />
			<EmployeePicker
				label="Participant"
				value={filters?.participant ?? ""}
				onChange={(newEmployeeValue) => {
					// Affirm that newEmployeeValue is of type SingleEmployeeSchema
					let singleNewEmployeeValue = newEmployeeValue;
					if (Array.isArray(newEmployeeValue)) {
						singleNewEmployeeValue = newEmployeeValue[0];
					}
					if (singleNewEmployeeValue && !("value" in singleNewEmployeeValue)) {
						return;
					}
					updateForm({ participant: singleNewEmployeeValue ?? undefined });
				}}
				isClearable
			/>
			<Spacer height={2} />
			<InputWrapper label="Previous Meeting" id="previous-meeting-filter">
				<DateRangePicker
					id="previous-meeting-filter"
					value={{
						from: filters.previousMeetingStart,
						to: filters.previousMeetingEnd,
					}}
					onChange={(date) => {
						updateForm({
							previousMeetingStart: date?.from ?? undefined,
							previousMeetingEnd: date?.to ?? undefined,
						});
					}}
					popoverContentClassName="z-[1100]"
					menuAlign="start"
					hasClearButton
				/>
			</InputWrapper>
			<Spacer height={2} />
			<InputWrapper label="Upcoming Meeting" id="upcoming-meeting-filter">
				<DateRangePicker
					id="upcoming-meeting-filter"
					value={{
						from: filters.upcomingMeetingStart,
						to: filters.upcomingMeetingEnd,
					}}
					onChange={(date) => {
						updateForm({
							upcomingMeetingStart: date?.from ?? undefined,
							upcomingMeetingEnd: date?.to ?? undefined,
						});
					}}
					popoverContentClassName="z-[1100]"
					menuAlign="start"
					hasClearButton
				/>
			</InputWrapper>
			<Spacer height={2} />
			<RadioButtons
				label={"Status"}
				value={statusRadioValue || null}
				options={statusRadioOptions}
				onChange={(radioOption) => {
					const radioOptionValue = radioOption.value as (typeof workspacesTableConfig.statusOptions)[number];
					if (!Array.from(workspacesTableConfig.statusOptions).includes(radioOptionValue)) {
						return;
					}
					updateForm({ status: radioOptionValue });
				}}
			/>
		</FilterPanel>
	);
};

export default WorkspacesFilterPanel;
